import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Card, CardContent, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

// Custom bar shape with rounded top corners
const CustomBarShape = (props: any) => {
  const { x, y, width, height, fill } = props;
  const radius = 4;
  return (
    <path
      d={`
        M ${x},${y + height} 
        L ${x},${y + radius} 
        Q ${x},${y} ${x + radius},${y} 
        L ${x + width - radius},${y} 
        Q ${x + width},${y} ${x + width},${y + radius} 
        L ${x + width},${y + height} 
        Z
      `}
      fill={fill}
    />
  );
};

interface DistanceWiseUtilizationGraphProps {
  startDate: string;
  endDate: string;
  fleetId?: string;
  tableGraphData?: any;
}

const DistanceWiseUtilizationGraph: React.FC<DistanceWiseUtilizationGraphProps> = ({
  startDate,
  endDate,
  fleetId,
  tableGraphData,
}) => {
  const theme = useTheme();

  // Sort data in ascending order (oldest first)
  const sortedData = useMemo(() => {
    if (
      tableGraphData &&
      tableGraphData.distanceWiseFleetUtilizationResponse
    ) {
      return [...tableGraphData.distanceWiseFleetUtilizationResponse].sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    }
    return [];
  }, [tableGraphData]);

  // Use ref for tracking the window start index.
  const windowStart = useRef(0);
  const [chartData, setChartData] = useState<{ name: string; value: number }[]>([]);
  const [dataRangeLabel, setDataRangeLabel] = useState("");
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // When sortedData changes, set windowStart to show last 6 days.
  useEffect(() => {
    if (sortedData.length > 0) {
      windowStart.current =
        sortedData.length > 6 ? sortedData.length - 6 : 0;
    } else {
      windowStart.current = 0;
    }
    updateChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData]);

  // Helper function to update chart data and the range label.
  const updateChartData = () => {
    if (sortedData.length > 0) {
      const start = windowStart.current;
      const windowSlice = sortedData.slice(start, start + 6);

      // Compute the range label.
      const startDateObj = new Date(windowSlice[0].date);
      const endDateObj = new Date(windowSlice[windowSlice.length - 1].date);
      const startLabel = startDateObj.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      });
      const endLabel = endDateObj.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      });
      const rangeLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      setDataRangeLabel(rangeLabel);

      // Map window slice to chart data (format date as "dd MMM").
      const transformedData = windowSlice.map((item: any) => ({
        name: new Date(item.date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        }),
        value: Math.round(item.utilization),
      }));
      setChartData(transformedData);

      setHasPrevious(start > 0);
      setHasNext(start + 6 < sortedData.length);
    } else {
      setChartData([]);
      setDataRangeLabel("");
      setHasPrevious(false);
      setHasNext(false);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      windowStart.current = Math.max(0, windowStart.current - 1);
      updateChartData();
    }
  };

  const handleNext = () => {
    if (hasNext) {
      windowStart.current = Math.min(
        sortedData.length - 6,
        windowStart.current + 1
      );
      updateChartData();
    }
  };

  return (
    <Card className="w-full h-full flex flex-col items-start">
      <Typography
        variant="button"
        color="secondary"
        fontWeight={400}
        className="mb-2"
      >
        Distance Wise Fleet Utilization Graph
      </Typography>
      <CardContent className="border rounded-xl border-[#F0EFF8] w-full">
        <div className="flex gap-2 pt-4">
          <div className="relative" style={{ left: "8px", top: "105px" }}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textAlign: "center",
              }}
            >
              Utilization (Km)
            </Typography>
          </div>
          <div className="flex-1 min-h-[200px] max-h-[400px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} barGap={10} barCategoryGap={20}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  cursor={false}
                  formatter={(value: number, name: string) => [`${value} %`, "Utilization"]}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                  itemStyle={{ color: theme.palette.text.primary }}
                />
                <Bar
                  dataKey="value"
                  fill={theme.palette.primary.main}
                  shape={<CustomBarShape />}
                  barSize={8}
                  activeBar={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Navigation Icons with Data Range Label */}
        {sortedData.length > 0 ? (
          <div className="w-full flex items-center justify-center gap-4 mt-2">
            <IconButton onClick={handlePrevious} disabled={!hasPrevious} size="small">
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography variant="caption" color="textPrimary" align="center">
              {dataRangeLabel}
            </Typography>
            <IconButton onClick={handleNext} disabled={!hasNext} size="small">
              <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <Typography
              variant="caption"
              color="textPrimary"
              align="center"
              className="mt-2 w-full"
            >
              Fleet Utilization Data
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DistanceWiseUtilizationGraph;